import React, { useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb, faRunning, faMapMarkerAlt,
  faClipboardList, faHeartbeat, faExclamationTriangle,
  faFireExtinguisher, faFirstAid
} from '@fortawesome/free-solid-svg-icons';
import EmergencyLighting from './EmergencyLighting/EmergencyLighting';
import EscapeRoutes from './EscapeRoutes/EscapeRoutes';
import AssemblyPoints from './AssemblyPoints/AssemblyPoints';
import EmergencyPlans from './EmergencyPlans/EmergencyPlans';
import Defibrillators from './Defibrillators/Defibrillators';
import IncidentReporting from './IncidentReporting/IncidentReporting';
import EmergencyPreparednessDashboard from './EmergencyPreparednessDashboard/EmergencyPreparednessDashboard';
import FireDrills from './FireDrills/FireDrills';
import FirstAidKits from './FirstAidKits/FirstAidKits';
import ComingSoonModal from '../../pages/ComingSoon/ComingSoonModal';
import './EmergencyPreparedness.css';

const SHOWCASE_MODE = false;
const COMING_SOON_SECTIONS = [
  'lighting', 'escape-routes', 'assembly-points', 'emergency-plans',
  'fire-drills', 'first-aid-kits', 'defibrillators', 'incident-reporting'
];

const sidebarSections = [
  { id: 'dashboard',          title: 'Emergency Dashboard',  icon: faClipboardList,       component: EmergencyPreparednessDashboard },
  { id: 'lighting',           title: 'Emergency Lighting',   icon: faLightbulb,           component: EmergencyLighting },
  { id: 'escape-routes',      title: 'Escape Routes',        icon: faRunning,             component: EscapeRoutes },
  { id: 'assembly-points',    title: 'Assembly Points',      icon: faMapMarkerAlt,        component: AssemblyPoints },
  { id: 'emergency-plans',    title: 'Emergency Plans',      icon: faClipboardList,       component: EmergencyPlans },
  { id: 'fire-drills',        title: 'Fire Drills',          icon: faFireExtinguisher,    component: FireDrills },
  { id: 'first-aid-kits',     title: 'First Aid Kits',       icon: faFirstAid,            component: FirstAidKits },
  { id: 'defibrillators',     title: 'Defibrillators',       icon: faHeartbeat,           component: Defibrillators },
  { id: 'incident-reporting', title: 'Incident Reporting',   icon: faExclamationTriangle, component: IncidentReporting },
];

const SUB_NAV_ITEMS = sidebarSections.map(s => ({
  id: s.id,
  label: s.title,
  icon: <FontAwesomeIcon icon={s.icon} />
}));

const EmergencyPreparedness = () => {
  const { id } = useParams();
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeatureName, setComingSoonFeatureName] = useState('');

  const ctx = useOutletContext() || {};
  const activeSubSection = ctx.activeSubSection || 'dashboard';
  const setActiveSubSection = ctx.setActiveSubSection || (() => {});

  const handleSectionClick = (section) => {
    if (SHOWCASE_MODE && COMING_SOON_SECTIONS.includes(section)) {
      const sectionData = sidebarSections.find(s => s.id === section);
      setComingSoonFeatureName(sectionData ? sectionData.title : 'This Feature');
      setShowComingSoonModal(true);
      return;
    }
    setActiveSubSection(section);
  };

  const renderContent = () => {
    const section = sidebarSections.find(s => s.id === activeSubSection);
    if (!section?.component) {
      const C = sidebarSections[0].component;
      return <C propertyId={id} onNavigate={handleSectionClick} />;
    }
    if (activeSubSection === 'dashboard') {
      const Component = section.component;
      return <Component propertyId={id} onNavigate={handleSectionClick} />;
    }
    const Component = section.component;
    return <Component propertyId={id} />;
  };

  const visiblePills = activeSubSection === 'dashboard'
    ? SUB_NAV_ITEMS.filter(i => i.id === 'dashboard')
    : SUB_NAV_ITEMS;

  const pillNav = (
    <nav className="rs-subnav">
      {visiblePills.map((item) => (
        <button
          key={item.id}
          className={`rs-pill${activeSubSection === item.id ? ' rs-pill-active' : ''}`}
          onClick={() => handleSectionClick(item.id)}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );

  return (
    <div className="emergency-preparedness-container">
      <div className="rs-subnav-mobile-wrap">{pillNav}</div>
      <div className="emergency-preparedness-content">
        <div className="emergency-preparedness-main">
          <div className="rs-subnav-desktop-wrap">{pillNav}</div>
          {renderContent()}
        </div>
      </div>
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={() => setShowComingSoonModal(false)}
        featureName={comingSoonFeatureName}
      />
    </div>
  );
};

export default EmergencyPreparedness;
